import type { FC, ReactNode } from 'react'
import type { Project } from '@/types'

import Link from 'next/link'

import { ArrowUpRight, CalendarCheck, MapPin } from 'lucide-react'

type WorkGalleryProps = {
    projects: Project[]
    variant?: 'page' | 'related'
    heading?: string
    subheading?: string
}

/**
 * Portfolio grid for the Work page, reused at the foot of each project
 * detail page through `variant='related'`.
 *
 * Each card is wrapped in `.work-list-item`, which is what the stagger
 * animates, mirroring how the ported card grids animate their `.w-dyn-item`.
 * GSAP leaves an inline transform on whatever it tweens, so keeping that on
 * the wrapper stops it overriding the card's own hover lift. The thumbnail
 * follows the same split: the reveal scales `.work-thumbnail-inner`, the
 * hover scales the image inside it.
 */
export const WorkGallery: FC<WorkGalleryProps> = ({
    projects,
    variant = 'page',
    heading,
    subheading
}): ReactNode => {
    const isRelated = variant === 'related'

    return (
        <section className={isRelated ? 'work-section section-padding' : 'work-section padding-bottom'}>
            <div className='w-layout-blockcontainer hero-container w-container'>
                <div className='section-content-wrapper'>
                    {heading ? (
                        <div className='section-title-wrapper align-center' data-reveal>
                            <h2 className='section-title'>
                                {heading}
                            </h2>

                            {subheading ? (
                                <p className='section-subtitle blog'>
                                    {subheading}
                                </p>
                            ) : null}
                        </div>
                    ) : null}

                    <div className='work-list' data-reveal-stagger='0.12'>
                        {projects.map((project) => (
                            <article key={project.id} className='work-list-item'>
                                <Link
                                    href={`/work/${project.slug}`}
                                    className='single-work w-inline-block'
                                    aria-label={`View project: ${project.title}`}
                                >
                                    <div className='work-thumbnail-wrapper'>
                                        <div className='work-thumbnail-inner' data-reveal='image'>
                                            <img
                                                src={project.image}
                                                loading='lazy'
                                                alt={project.imageAlt}
                                                className='work-thumbnail'
                                            />
                                        </div>

                                        <p
                                            className='work-tag'
                                            data-mdlfy-cms={`projects:${project.id}:project_type`}
                                        >
                                            {project.projectType}
                                        </p>
                                    </div>

                                    <div className='work-content'>
                                        <div className='work-title-wrapper'>
                                            <div className='work-title-row'>
                                                <p
                                                    className='work-title'
                                                    data-mdlfy-cms={`projects:${project.id}:title`}
                                                >
                                                    {project.title}
                                                </p>

                                                <span className='work-title-arrow' aria-hidden='true'>
                                                    <ArrowUpRight className='size-5' />
                                                </span>
                                            </div>

                                            <p
                                                className='work-summary'
                                                data-mdlfy-cms={`projects:${project.id}:summary`}
                                            >
                                                {project.summary}
                                            </p>
                                        </div>

                                        <div className='work-meta'>
                                            <p className='work-meta-item'>
                                                <MapPin
                                                    className='size-4 work-meta-icon'
                                                    aria-hidden='true'
                                                />

                                                <span data-mdlfy-cms={`projects:${project.id}:location`}>
                                                    {project.location}
                                                </span>
                                            </p>

                                            {project.completedYear ? (
                                                <p className='work-meta-item'>
                                                    <CalendarCheck
                                                        className='size-4 work-meta-icon'
                                                        aria-hidden='true'
                                                    />

                                                    <span className='work-meta-label'>
                                                        Completed
                                                    </span>

                                                    <span
                                                        data-mdlfy-cms={`projects:${project.id}:completed_year`}
                                                    >
                                                        {project.completedYear}
                                                    </span>
                                                </p>
                                            ) : null}
                                        </div>
                                    </div>
                                </Link>
                            </article>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}
